import React, { useState } from 'react';

import { Stack, Typography, Box, Button } from '@mui/material';
import { BasePage } from '../../components/base-page';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  CreateContractDocument,
  CreateContractMutation,
  CreateContractMutationVariables,
  GetContractsDocument,
  GetContractsQuery,
} from '../../generated/graphql';
import { useNavigate, useSearchParams } from 'react-router';
import { appPaths } from '../../app-paths';
import { ContractCard } from '../../components/contract-card';
import { NewCard } from '../../components/new-card';
import { useAlert } from '@dwelvio/react-alert';

const FilterTypesArray = ['draft', 'active', 'terminated', 'archived'] as const;
type FilterType = (typeof FilterTypesArray)[number];

export function ContractListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { show } = useAlert();
  const { data: contData, loading, error, refetch } = useQuery<GetContractsQuery>(GetContractsDocument);

  const filterGroup = (searchParams.get('fg') as FilterType) ?? 'active';
  if (filterGroup && FilterTypesArray.includes(filterGroup as FilterType) === false) {
    searchParams.set('fg', 'active');
    setSearchParams(searchParams);
  }

  React.useEffect(() => {
    if (error) {
      show.error('Nepodarilo sa načítať prenájmy.', error.message);
    }
  }, [error, show]);

  const [createContract] = useMutation<CreateContractMutation, CreateContractMutationVariables>(
    CreateContractDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa vytvoriť prenájom', e.message),
      refetchQueries: [{ query: GetContractsDocument }],
      awaitRefetchQueries: true,
    },
  );

  const contracts = contData?.getContracts ?? [];

  // Filter contracts based on toggle states
  const filteredContracts = React.useMemo(
    () =>
      contracts.filter((contract) => {
        // Always exclude deleted contracts
        if (contract.deletedAt) return false;

        // Check if contract is terminated (has terminationDate or endDate in the past)
        const isTerminated =
          contract.terminationDate || (contract.endDate && new Date(contract.endDate).getTime() < Date.now());

        // Check if contract is signed
        const isDraft = !contract.signingDate;

        // Apply filters
        if (isTerminated && filterGroup !== 'terminated') return false;
        if (isDraft && filterGroup !== 'draft') return false;
        if (!isTerminated && !isDraft && filterGroup !== 'active') return false;

        return true;
      }),
    [contracts, filterGroup],
  );

  // Sort contracts ascending by title
  const sortedContracts = React.useMemo(
    () => [...filteredContracts].sort((a, b) => a.title.localeCompare(b.title)),
    [filteredContracts],
  );

  const handleCreateContract = async () => {
    const nc = await createContract();
    await refetch();
    if (nc.data?.createContract.contract?.id) {
      navigate(appPaths.contract.navigate({ contractId: nc.data.createContract.contract.id }));
    }
  };

  return (
    <BasePage title="Prenájmy" loading={loading} errorMsg={error ? 'Nepodarilo sa načítať prenájmy.' : undefined}>
      {/* Contract Filters */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          Všetky prenájmy
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant={filterGroup != 'active' ? 'outlined' : 'contained'}
            onClick={() => {
              searchParams.set('fg', 'active');
              setSearchParams(searchParams);
            }}
          >
            Aktívne
          </Button>
          <Button
            variant={filterGroup != 'draft' ? 'outlined' : 'contained'}
            onClick={() => {
              searchParams.set('fg', 'draft');
              setSearchParams(searchParams);
            }}
          >
            Návrhy
          </Button>
          <Button
            variant={filterGroup != 'terminated' ? 'outlined' : 'contained'}
            onClick={() => {
              searchParams.set('fg', 'terminated');
              setSearchParams(searchParams);
            }}
          >
            Ukončené
          </Button>
        </Stack>

        {/* Filtered contracts list */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap flexWrap="wrap" sx={{ width: '100%' }}>
          {sortedContracts.length === 0 && (
            <Typography variant="body1" sx={{ width: '100%' }}>
              Žiadne prenájmy nezodpovedajú vybraným filtrom.
            </Typography>
          )}
          {sortedContracts.map((contract) => (
            <ContractCard
              key={contract.id}
              contract={contract}
              onClick={(selectedContract) => navigate(appPaths.contract.navigate({ contractId: selectedContract.id }))}
              width={280}
              height={260}
            />
          ))}
          <NewCard label="Pridaj" onClick={handleCreateContract} height={260} width={280} />
        </Stack>
      </Box>
    </BasePage>
  );
}
